'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';

import ConfirmDialog from '@/components/ui/ConfirmDialog';
import NotificationDropdown from '@/components/common/NotificationDropdown';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const pathname = usePathname();
  const router = useRouter();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const { user, userProfile, signOut, isAdmin } = useAuth();

  const navigationItems = [
    { label: 'Home', href: '/home', icon: 'HomeIcon' },
    { label: 'Discover Pets', href: '/pet-discovery', icon: 'MagnifyingGlassIcon' },
    { label: 'Care Guides', href: '/care-guides-hub', icon: 'BookOpenIcon' },
    { label: 'Adoption Process', href: '/adoption-process', icon: 'ClipboardDocumentCheckIcon' },
    { label: 'Emergency', href: '/emergency-resources', icon: 'ExclamationTriangleIcon' },
  ];

  // Helper function for link classes
  const getLinkClasses = (href: string, isMobile = false) => {
    // Handle root path redirect logic
    const effectivePathname = pathname === '/' ? '/home' : pathname;
    const isActive = effectivePathname === href || (href !== '/home' && effectivePathname?.startsWith(href));

    const baseClasses = "transition-all duration-200 font-medium relative group";

    if (isMobile) {
      return `flex items-center space-x-3 px-4 py-3 rounded-lg ${isActive
        // Use bg-gray-100 as fallback since bg-primary/10 needs RGB var configuration
        ? 'bg-gray-100 text-primary font-semibold border-l-4 border-primary'
        : 'text-foreground hover:bg-muted hover:text-primary'
        }`;
    }

    return `${baseClasses} px-3 py-2 ${isActive
      ? 'text-primary'
      : 'text-gray-700 hover:text-primary'
      }`;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSignOutClick = () => {
    setShowSignOutConfirm(true);
    setIsMobileMenuOpen(false);
  };

  const handleSignOutConfirm = async () => {
    const { error } = await signOut();
    setShowSignOutConfirm(false);

    if (error) {
      toast.error(`Failed to sign out: ${error.message}. Please try again.`);
    } else {
      toast.success('Successfully signed out');
      router.push('/login');
    }
  };

  return (
    <>
      <header className={`sticky top-0 z-50 w-full bg-card/95 backdrop-blur-md shadow-sm border-b border-border/40 ${className}`}>
        {/* ... existing header content ... */}
        <div className="w-full">
          <div className="flex items-center justify-between h-16 px-4 lg:px-8">
            {/* ... logo ... */}
            <Link href="/home" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <div className="relative w-10 h-10 flex-shrink-0">
                <Image
                  src="/images/logo.png"
                  alt="Exotic and Domestic Logo"
                  fill
                  className="object-contain"
                />
              </div>
              <span className="text-2xl font-headline font-semibold text-primary tracking-tight">ExoticAndDomestic</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => {
                const effectivePathname = pathname === '/' ? '/home' : pathname;
                const isActive = effectivePathname === item.href || (item.href !== '/home' && effectivePathname?.startsWith(item.href));
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={getLinkClasses(item.href)}
                  >
                    <span className="relative z-10">{item.label}</span>
                    {isActive && (
                      <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-primary rounded-full" />
                    )}
                  </Link>
                );
              })}

              {isAdmin() && (
                <Link
                  href="/admin/dashboard"
                  className="text-purple-700 hover:text-purple-600 font-semibold transition-colors flex items-center gap-1 px-3 py-2"
                >
                  <Icon name="CogIcon" size={16} variant="outline" />
                  Admin
                </Link>
              )}
            </nav>

            <div className="hidden lg:flex items-center space-x-4">
              {user ? (
                <div className="flex items-center gap-3">
                  <NotificationDropdown />
                  <Link href="/dashboard" className="text-sm text-gray-700 font-medium hover:text-primary transition-colors">
                    {userProfile?.full_name || 'User'}
                  </Link>
                  <div className="h-4 w-px bg-gray-300"></div>
                  <button
                    onClick={handleSignOutClick}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors hover:bg-gray-100 rounded-md"
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <>
                  <Link
                    href="/login"
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link
                    href="/pet-discovery"
                    className="px-6 py-2.5 bg-brand-cta text-white font-headline font-semibold rounded-lg hover:shadow-elevated hover:-translate-y-0.5 transition-all duration-300 shadow-md"
                  >
                    Find Your Companion
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Actions */}
            <div className="flex items-center gap-2 lg:hidden">
              {user && <NotificationDropdown />}
              <button
                onClick={toggleMobileMenu}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
                aria-label="Toggle mobile menu"
              >
                <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} variant="outline" />
              </button>
            </div>
          </div>

          {isMobileMenuOpen && (
            <div className="lg:hidden bg-card border-t border-border shadow-lg">
              <nav className="flex flex-col py-4 px-4 space-y-2">
                {navigationItems.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={getLinkClasses(item.href, true)}
                  >
                    <Icon name={item.icon as any} size={20} variant="outline" />
                    <span className="font-body font-medium">{item.label}</span>
                  </Link>
                ))}

                <div className="h-px bg-gray-200 my-2 mx-4" />

                {isAdmin() && (
                  <Link
                    href="/admin/dashboard"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center space-x-3 px-4 py-3 rounded-lg text-purple-700 hover:bg-purple-50 transition-all duration-300"
                  >
                    <Icon name="CogIcon" size={20} variant="outline" />
                    <span className="font-body font-semibold">Admin Dashboard</span>
                  </Link>
                )}

                {user ? (
                  <>
                    <Link
                      href="/dashboard"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-all duration-300"
                    >
                      <Icon name="UserIcon" size={20} variant="outline" />
                      <span className="font-body font-medium">My Dashboard</span>
                    </Link>
                    <Link
                      href="/settings"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-all duration-300"
                    >
                      <Icon name="Cog6ToothIcon" size={20} variant="outline" />
                      <span className="font-body font-medium">Settings</span>
                    </Link>

                    <button
                      onClick={handleSignOutClick}
                      className="mt-4 px-6 py-3 bg-gray-100 text-gray-900 font-headline font-semibold rounded-lg text-center transition-all duration-300 hover:bg-gray-200"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      href="/login"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="mt-4 px-6 py-3 bg-gray-200 text-gray-900 font-headline font-semibold rounded-lg text-center transition-all duration-300"
                    >
                      Sign In
                    </Link>
                    <Link
                      href="/pet-discovery"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="px-6 py-3 bg-brand-cta text-white font-headline font-semibold rounded-lg text-center hover:shadow-elevated transition-all duration-300"
                    >
                      Find Your Companion
                    </Link>
                  </>
                )}
              </nav>
            </div>
          )}
        </div >
      </header >

      <ConfirmDialog
        isOpen={showSignOutConfirm}
        onClose={() => setShowSignOutConfirm(false)}
        onConfirm={handleSignOutConfirm}
        title="Sign Out"
        message="Are you sure you want to sign out?"
        confirmText="Sign Out"
        variant="default" // Non-destructive as signing out is reversible/safe
      />
    </>
  );
};

export default Header;